/**
 * Unified API Service
 * 
 * This service ensures all API calls go through the same authentication
 * and error handling pipeline. It replaces direct fetch calls and ensures
 * consistent token expiration handling across the entire application.
 */

import axios, { AxiosInstance, AxiosError, AxiosRequestConfig, InternalAxiosRequestConfig } from 'axios';
import { enhancedTokenManager } from './EnhancedTokenManager';
import { config } from '../config/environment';

// Extend InternalAxiosRequestConfig to include custom flags
interface ExtendedAxiosRequestConfig extends InternalAxiosRequestConfig {
  _retry?: boolean;
  _skipAuth?: boolean; // For public endpoints
}

class UnifiedApiService {
  private api: AxiosInstance;
  private static instance: UnifiedApiService;

  private constructor() {
    // Create axios instance
    this.api = axios.create({
      baseURL: config.apiUrl,
      headers: {
        'Content-Type': 'application/json',
      },
      withCredentials: true,
      timeout: config.apiTimeout,
    });

    this.setupInterceptors();
  }

  static getInstance(): UnifiedApiService {
    if (!UnifiedApiService.instance) {
      UnifiedApiService.instance = new UnifiedApiService();
    }
    return UnifiedApiService.instance;
  }

  private setupInterceptors() {
    // Request interceptor - add token to all requests
    this.api.interceptors.request.use(
      async (config: InternalAxiosRequestConfig) => {
        const extendedConfig = config as ExtendedAxiosRequestConfig;
        
        // Skip auth for public endpoints
        if (extendedConfig._skipAuth) {
          return config;
        }

        // Get token from enhanced token manager
        const token = await enhancedTokenManager.getToken();
        
        if (token) {
          config.headers = config.headers || {};
          config.headers.Authorization = `Bearer ${token}`;
          
          // Update activity on API calls to prevent idle timeout
          // This ensures any API interaction counts as user activity
          enhancedTokenManager.updateActivity();
        } else if (!this.isPublicEndpoint(config.url || '')) {
          // No token available for protected endpoint
          // Token manager will handle redirect
          return Promise.reject(new Error('No authentication token available'));
        }
        
        // PRODUCTION: Add selected church ID header from backend preferences or localStorage
        // This enables multi-church context switching using backend-stored preferences
        // Falls back to localStorage for cases where useChurch hook sets it (e.g., single church auto-select)
        const user = await import('../utils/authStateManager').then(m => m.authStateManager.getUser());
        let selectedChurchId = user?.preferences?.selectedChurchId || localStorage.getItem('selectedChurchId');

        // Guard: extract .id if church is an object (serializer returns {id, name})
        if (selectedChurchId && typeof selectedChurchId === 'object') {
          selectedChurchId = (selectedChurchId as any).id || null;
        }
        // Guard: reject invalid values like "[object Object]"
        if (selectedChurchId && (selectedChurchId === '[object Object]' || String(selectedChurchId).includes('[object'))) {
          localStorage.removeItem('selectedChurchId');
          selectedChurchId = null;
        }

        if (selectedChurchId) {
          config.headers = config.headers || {};
          config.headers['X-Selected-Church-ID'] = String(selectedChurchId);

          // Debug logging for church header
          if (config.url?.includes('impact_dashboard') || config.url?.includes('/users/') || config.url?.includes('/leaders/')) {
            console.log('[UnifiedApiService] Adding church header:', {
              churchId: selectedChurchId,
              source: user?.preferences?.selectedChurchId ? 'backend' : 'localStorage',
              url: config.url,
              method: config.method
            });
          }
        } else if (config.url?.includes('impact_dashboard') || config.url?.includes('/users/') || config.url?.includes('/leaders/')) {
          console.warn('[UnifiedApiService] No selectedChurchId found (checked backend preferences and localStorage) for request:', config.url);
        }

        // Add CSRF token for non-GET requests
        if (config.method !== 'get') {
          const csrfToken = this.getCsrfToken();
          if (csrfToken) {
            config.headers = config.headers || {};
            config.headers['X-CSRFToken'] = csrfToken;
          }
        }

        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    // Response interceptor - handle 401 and 402 errors
    this.api.interceptors.response.use(
      (response) => response,
      async (error: AxiosError) => {
        const originalRequest = error.config as ExtendedAxiosRequestConfig;

        // Handle 402 Payment Required - Subscription limit reached
        if (error.response?.status === 402) {
          const errorData = error.response.data as { error?: string; code?: string };
          const message = errorData?.error || 'Subscription limit reached. Please upgrade your plan.';
          const code = errorData?.code || 'SUBSCRIPTION_LIMIT_REACHED';

          // Dispatch custom event for subscription limit errors
          // Components can listen for this to show upgrade modal
          window.dispatchEvent(new CustomEvent('subscription-limit-reached', {
            detail: {
              message,
              code,
              url: originalRequest?.url
            }
          }));

          // Still reject so the calling code can handle it too
          return Promise.reject(error);
        }

        // Handle 401 Unauthorized
        if (error.response?.status === 401 && !originalRequest._retry) {
          originalRequest._retry = true;

          // Check if this is a refresh endpoint failure
          if (originalRequest.url?.includes('/token/refresh')) {
            // Refresh failed, token manager will handle redirect
            return Promise.reject(error);
          }

          // Skip platform token refresh for member/public requests (they use _skipAuth)
          // These requests manage their own tokens (e.g., connectpath_member_token)
          if (originalRequest._skipAuth) {
            // Don't try to refresh with platform token manager
            // Let the calling code handle the 401 (e.g., clear member token and show login)
            return Promise.reject(error);
          }

          try {
            // Try to refresh token
            await enhancedTokenManager.refreshToken();

            // Retry original request with new token
            const token = await enhancedTokenManager.getToken();
            if (token && originalRequest.headers) {
              originalRequest.headers.Authorization = `Bearer ${token}`;
            }

            return this.api(originalRequest);
          } catch (refreshError) {
            // Refresh failed, enhanced token manager will handle redirect
            return Promise.reject(refreshError);
          }
        }

        // Handle other errors
        return Promise.reject(error);
      }
    );
  }

  private getCsrfToken(): string | null {
    const cookie = document.cookie
      .split('; ')
      .find(row => row.startsWith(`${config.csrfCookieName}=`));
    
    return cookie ? cookie.split('=')[1] : null;
  }

  private isPublicEndpoint(url: string): boolean {
    const publicEndpoints = [
      '/auth/login',
      '/auth/register',
      '/auth/logout',  // Logout should work even when token is expired
      '/auth/password/reset',
      '/auth/password/reset/confirm',
    ];

    return publicEndpoints.some(endpoint => url.includes(endpoint));
  }

  // Public API methods

  /**
   * GET request
   */
  async get<T = any>(url: string, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.api.get<T>(url, config);
    return response.data;
  }

  /**
   * POST request
   */
  async post<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.api.post<T>(url, data, config);
    return response.data;
  }

  /**
   * PUT request
   */
  async put<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.api.put<T>(url, data, config);
    return response.data;
  }

  /**
   * PATCH request
   */
  async patch<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.api.patch<T>(url, data, config);
    return response.data;
  }

  /**
   * DELETE request
   */
  async delete<T = any>(url: string, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.api.delete<T>(url, config);
    return response.data;
  }

  /**
   * Make a request to a public endpoint (no auth required)
   */
  async publicRequest<T = any>(
    method: 'get' | 'post' | 'put' | 'patch' | 'delete',
    url: string,
    data?: any,
    config?: AxiosRequestConfig
  ): Promise<T> {
    const extendedConfig: AxiosRequestConfig & { _skipAuth: boolean } = {
      ...config,
      _skipAuth: true,
    };

    switch (method) {
      case 'get':
        return this.get<T>(url, extendedConfig);
      case 'post':
        return this.post<T>(url, data, extendedConfig);
      case 'put':
        return this.put<T>(url, data, extendedConfig);
      case 'patch':
        return this.patch<T>(url, data, extendedConfig);
      case 'delete':
        return this.delete<T>(url, extendedConfig);
    }
  }

  /**
   * Get the axios instance for advanced usage
   */
  getAxiosInstance(): AxiosInstance {
    return this.api;
  }
}

// Export singleton instance
export const unifiedApi = UnifiedApiService.getInstance();

// Helper function for error handling
export const handleApiError = (error: any): string => {
  if (error.response) {
    // The request was made and the server responded with an error status
    if (error.response.data && error.response.data.message) {
      return error.response.data.message;
    }
    if (error.response.data && error.response.data.detail) {
      return error.response.data.detail;
    }
    return `Error: ${error.response.status} ${error.response.statusText}`;
  } else if (error.request) {
    // The request was made but no response was received
    return 'No response received from server. Please check your connection.';
  } else {
    // Something happened in setting up the request
    return error.message || 'An unknown error occurred';
  }
};